/**
 * BetaFeedbackPrompt Component
 * 
 * In-app feedback loop that appears after key actions.
 * Supports voice or text input with immediate transcription.
 * Module 10B implementation.
 * 
 * Usage:
 *   <BetaFeedbackPrompt 
 *     pageKey="weekly_close" 
 *     actionKey="weekly_close_finalized" 
 *   />
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, MessageSquare, Send, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';

// Emotions available for selection
const EMOTIONS = [
  { key: 'calm', label: 'Calm', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { key: 'clear', label: 'Clear', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { key: 'confusing', label: 'Confusing', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { key: 'heavy', label: 'Heavy', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  { key: 'not_sure', label: 'Not sure', color: 'bg-slate-100 text-slate-700 border-slate-200' },
];

// Consent key for localStorage
const CONSENT_STORAGE_KEY = 'ara_feedback_consent';

export function BetaFeedbackPrompt({ 
  pageKey, 
  actionKey, 
  onClose,
  initialExpanded = false,
  className = '',
}) {
  // Feature flags
  const [flags, setFlags] = useState({ enabled: false, voice_enabled: false, require_consent: true });
  const [flagsLoaded, setFlagsLoaded] = useState(false);
  
  // UI state
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [inputMode, setInputMode] = useState('text'); // 'text' | 'voice'
  const [text, setText] = useState('');
  const [hasConsented, setHasConsented] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  
  // Load feature flags
  useEffect(() => {
    const loadFlags = async () => {
      try {
        const response = await api.feedback.getFlags();
        if (response.ok) {
          const data = await response.json();
          setFlags(data);
        } else {
          // If flags fail to load, assume disabled
          setFlags({ enabled: false, voice_enabled: false, require_consent: true });
        }
      } catch (err) {
        // If flags fail to load, assume disabled
        setFlags({ enabled: false, voice_enabled: false, require_consent: true });
      }
      setFlagsLoaded(true);
    };
    loadFlags();
  }, []);
  
  // Load consent from localStorage
  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (storedConsent === 'true') {
      setHasConsented(true);
      setConsentChecked(true);
    }
  }, []);
  
  // Don't render if flags not loaded or feedback disabled
  if (!flagsLoaded || !flags.enabled) {
    return null;
  }
  
  // Don't show if already submitted
  if (submitted) {
    return (
      <div 
        data-testid="feedback-success-badge"
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm text-emerald-700 bg-emerald-50 rounded-full ${className}`}
      >
        <Check className="w-4 h-4" />
        Reflection saved
      </div>
    );
  }
  
  // Handle voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      toast.error('Could not access microphone');
      console.error('Recording error:', err);
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };
  
  const handleSubmit = async () => {
    // Validate consent
    if (flags.require_consent && !consentChecked) {
      toast.error('Please check the consent box to continue');
      return;
    }
    
    // Validate emotion
    if (!selectedEmotion) {
      toast.error('Please select how this experience felt');
      return;
    }
    
    // Validate input
    if (inputMode === 'text' && !text.trim()) {
      toast.error('Please enter your reflection');
      return;
    }
    
    if (inputMode === 'voice' && !audioBlob) {
      toast.error('Please record your reflection');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let response;
      
      if (inputMode === 'voice') {
        response = await api.feedback.submitVoice({
          emotion: selectedEmotion,
          pageKey,
          actionKey,
          audioBlob,
          consent: consentChecked,
        });
      } else {
        response = await api.feedback.submitText({
          emotion: selectedEmotion,
          pageKey,
          actionKey,
          text: text.trim(),
          consent: consentChecked,
        });
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Submission failed');
      }
      
      // Save consent preference
      if (consentChecked && !hasConsented) {
        localStorage.setItem(CONSENT_STORAGE_KEY, 'true');
        setHasConsented(true);
      }
      
      setSubmitted(true);
      toast.success('Saved. Thank you.');
      
      // Notify parent if needed
      if (onClose) {
        setTimeout(() => onClose(), 1500);
      }
      
    } catch (err) {
      console.error('Feedback submission error:', err);
      toast.error('Could not save reflection. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Collapsed state
  if (!isExpanded) {
    return (
      <button
        data-testid="feedback-prompt-collapsed"
        onClick={() => setIsExpanded(true)}
        className={`flex items-center gap-2 px-4 py-2 text-sm text-slate-600 bg-slate-50 hover:bg-slate-100 
          rounded-lg border border-slate-200 transition-colors ${className}`}
      >
        <MessageSquare className="w-4 h-4 text-slate-500" />
        <span>Reflect (30 seconds)</span>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>
    );
  }
  
  // Expanded state
  return (
    <div 
      data-testid="feedback-prompt-expanded"
      className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <h3 className="text-sm font-medium text-slate-700">Quick reflection</h3>
        <button 
          data-testid="feedback-close-btn"
          onClick={() => setIsExpanded(false)}
          className="p-1 text-slate-400 hover:text-slate-600 rounded"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Emotion Selection */}
        <div>
          <label className="block text-xs text-slate-500 mb-2">How did this feel?</label>
          <div className="flex flex-wrap gap-2" data-testid="emotion-selection">
            {EMOTIONS.map(({ key, label, color }) => (
              <button
                key={key}
                data-testid={`emotion-${key}`}
                onClick={() => setSelectedEmotion(key)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-all
                  ${selectedEmotion === key 
                    ? `${color} ring-2 ring-offset-1 ring-slate-400` 
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Input Mode Toggle */}
        <div className="flex items-center gap-2">
          <button
            data-testid="input-mode-voice"
            onClick={() => setInputMode('voice')}
            disabled={!flags.voice_enabled}
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all
              ${inputMode === 'voice' 
                ? 'bg-slate-800 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }
              ${!flags.voice_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Mic className="w-4 h-4" />
            Speak
          </button>
          <button
            data-testid="input-mode-text"
            onClick={() => setInputMode('text')}
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all
              ${inputMode === 'text' 
                ? 'bg-slate-800 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            <MessageSquare className="w-4 h-4" />
            Type
          </button>
        </div>
        
        {/* Voice Recording Interface */}
        {inputMode === 'voice' && flags.voice_enabled && (
          <div className="space-y-3" data-testid="voice-recording-interface">
            <div className="flex items-center justify-center gap-4 py-4 bg-slate-50 rounded-lg">
              {isRecording ? (
                <>
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-lg font-mono text-slate-700">{formatTime(recordingTime)}</span>
                  <Button
                    data-testid="stop-recording-btn"
                    onClick={stopRecording}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <MicOff className="w-4 h-4" />
                    Stop
                  </Button>
                </>
              ) : audioBlob ? (
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-slate-600">Recording saved ({formatTime(recordingTime)})</span>
                  <Button
                    data-testid="re-record-btn"
                    onClick={() => { setAudioBlob(null); startRecording(); }}
                    variant="ghost"
                    size="sm"
                  >
                    Re-record
                  </Button>
                </div>
              ) : (
                <Button
                  data-testid="start-recording-btn"
                  onClick={startRecording}
                  variant="outline"
                  className="gap-2"
                >
                  <Mic className="w-4 h-4" />
                  Start recording
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Text Input Interface */}
        {inputMode === 'text' && (
          <div data-testid="text-input-interface">
            <Textarea
              data-testid="feedback-text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="A few words about your experience..."
              className="min-h-[80px] resize-none"
              maxLength={500}
            />
            <p className="text-xs text-slate-400 mt-1 text-right">{text.length}/500</p>
          </div>
        )}
        
        {/* Consent */}
        {flags.require_consent && !hasConsented && (
          <div className="flex items-start gap-2" data-testid="consent-section">
            <input
              type="checkbox"
              id="feedback-consent"
              data-testid="consent-checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="mt-1 rounded border-slate-300 text-slate-800 focus:ring-slate-500"
            />
            <label htmlFor="feedback-consent" className="text-xs text-slate-500 leading-relaxed">
              Your reflection helps improve the beta. You can keep it anonymous. 
              I consent to share this reflection with the Ara Wealth team.
            </label>
          </div>
        )}
        
        {/* Microcopy */}
        <p className="text-xs text-slate-400 text-center">
          No perfect wording needed. We'll tidy it up.
        </p>
        
        {/* Submit */}
        <Button
          data-testid="feedback-submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitting || isRecording}
          className="w-full gap-2"
        >
          {isSubmitting ? (
            <>Saving...</>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default BetaFeedbackPrompt;
